import * as React from 'react';

import type { Node } from 'unist';
import RMarkdown, { Options } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { glyphs } from '@/lib/shared/glyphs';
import { HeadingLink } from './heading-link';
import { slugify } from '@/lib/shared/utils/slugify';
import { CodeBlock } from './code-block';
import { isSupportedLanguage } from '@/lib/frontend/highlighter';
import { visit } from 'unist-util-visit';
import type { Text, Parent } from 'mdast';
import { JSX } from 'react';
import { RelativeLink } from './markdown/relative-link';
import { LividVideo, lividVideoTag, type LividVideoProps } from './markdown/livid-video';
import { parseLanguage } from '@/lib/shared/utils/language';
import { Code } from './typography';
import { cn } from '@/lib/shared/ui';

type Props = {
  children: string;
  options?: Options;
};

const glyphTag = 'glyph-icon';

type GlyphIconProps = {
  'data-name': string;
  children?: React.ReactNode;
};

function remarkProcessFigures() {
  return (tree: Node) => {
    visit(tree, 'text', (node: Text, index: number, parent: Parent) => {
      if (node.value && typeof node.value === 'string') {
        const figureRegex = /:fig-([a-zA-Z0-9-_]+):/g;
        const matches = [...node.value.matchAll(figureRegex)];

        if (matches.length === 0) {
          return;
        }

        const newNodes: (Text | { type: 'html'; value: string })[] = [];
        let lastIndex = 0;

        matches.forEach((match) => {
          const [fullMatch, figureName] = match;
          const matchIndex = match.index!;

          // Add text before the match
          if (matchIndex > lastIndex) {
            newNodes.push({
              type: 'text',
              value: node.value.slice(lastIndex, matchIndex),
            });
          }

          // Create a custom element that will be handled by components
          newNodes.push({
            type: 'html',
            value: `<${glyphTag} data-name="${figureName}"></${glyphTag}>`,
          });

          lastIndex = matchIndex + fullMatch.length;
        });

        // Add remaining text
        if (lastIndex < node.value.length) {
          newNodes.push({
            type: 'text',
            value: node.value.slice(lastIndex),
          });
        }

        // Replace the current node with new nodes
        if (parent && typeof index === 'number') {
          parent.children.splice(index, 1, ...newNodes);
          return index + newNodes.length - 1;
        }
      }
    });
  };
}

const getTextContent = (children: React.ReactNode): string => {
  if (typeof children === 'string') {
    return children;
  }

  if (Array.isArray(children)) {
    return children.map((child) => getTextContent(child)).join('');
  }

  if (React.isValidElement(children)) {
    const element = children as React.ReactElement<{ children?: React.ReactNode }>;
    return getTextContent(element.props.children);
  }

  return '';
};

const wrapLink = (tag: string, children: React.ReactNode) => {
  const Tag = tag as keyof JSX.IntrinsicElements;

  const slug = slugify(getTextContent(children));

  return (
    <Tag className="group relative" id={slug}>
      {children}

      <HeadingLink slug={slug} />
    </Tag>
  );
};

// Renders markdown with the shared remark/rehype plugins but no special component overrides
export const ReactMarkdownBase = (props: Props) => {
  const { children, options } = props;

  if (children == null) {
    return null;
  }

  return (
    <RMarkdown
      {...options}
      remarkPlugins={[
        remarkGfm, // Enable github-flavored markdown
        remarkProcessFigures,
        ...(options?.remarkPlugins || []),
      ]}
      rehypePlugins={[
        rehypeRaw, // Enable raw HTML tags
        ...(options?.rehypePlugins || []),
      ]}
    >
      {children}
    </RMarkdown>
  );
};

export const ReactMarkdown = (props: Props) => {
  const { children, options } = props;

  if (children == null) {
    return null;
  }

  return (
    <ReactMarkdownBase
      options={{
        ...options,
        components: {
          // Custom glyph tag handler
          [glyphTag as keyof JSX.IntrinsicElements]: (props: GlyphIconProps) => {
            const figureName = props['data-name'] as string;
            const key = figureName.replace('fig-', '');
            const svg =
              glyphs[key as keyof typeof glyphs] ||
              glyphs[key.toLowerCase() as keyof typeof glyphs];

            if (!svg) {
              console.warn(`Missing glyph: ${figureName}`);
              return <span className="text-red-500">[Missing: {figureName}]</span>;
            }

            return (
              <span
                className="[&_svg]:fill-background"
                data-name={figureName}
                dangerouslySetInnerHTML={{ __html: svg }}
              />
            );
          },
          // Custom livid video embed tag handler
          [lividVideoTag as keyof JSX.IntrinsicElements]: (props: LividVideoProps) => (
            <LividVideo id={props.id} title={props.title} ratio={props.ratio} />
          ),
          h1: ({ children }) => wrapLink('h1', children),
          h2: ({ children }) => wrapLink('h2', children),
          h3: ({ children }) => wrapLink('h3', children),
          h4: ({ children }) => wrapLink('h4', children),
          h5: ({ children }) => wrapLink('h5', children),
          code: (props) => {
            const { className } = props;

            const language = parseLanguage(className || '');
            if (language && isSupportedLanguage(language)) {
              const textContent = getTextContent(props.children);
              return <CodeBlock language={language} code={textContent} className="my-4" />;
            }

            // Fenced code blocks without a language always end with '\n'; inline code never does
            const textContent = getTextContent(props.children);
            if (textContent.endsWith('\n')) {
              return (
                <pre className="bg-[#24292e] rounded-lg font-mono text-sm text-white p-5 my-4 overflow-x-auto">
                  <code>{textContent}</code>
                </pre>
              );
            }

            return <Code {...props} className={cn('inline-code')} />;
          },
          a(props) {
            return <RelativeLink {...props} />;
          },
          img({ src, alt, title }) {
            return <img src={src} alt={alt} title={title} loading="lazy" />;
          },
          ...options?.components, // Allow passing in custom component overrides
        },
      }}
    >
      {children}
    </ReactMarkdownBase>
  );
};
